'use client'

// Import the crypto icons
import { useMemo, useState } from 'react'
import styles from './TerminalTabs.module.css'
import { useCoinGeckoPrices } from '../../../hooks/use-coin-gecko-prices'
import { useBinanceUserAssets } from './hooks/useBinanceData'
import Image from 'next/image'
import DepositWithdrawModal from './DepositWithdrawModal'
import React from 'react'
import { usePathname } from 'next/navigation'

/**
 * @uiComponent
 * @name Holdings
 * @description The Holdings component displays the user's cryptocurrency portfolio on the Binance platform.
 * It shows detailed information about each crypto asset including balances, current prices,
 * 24-hour price changes, and portfolio allocation. Features real-time pricing from CoinGecko,
 * clickable asset rows for deposit/withdraw actions, and comprehensive portfolio analytics.
 * @location Holdings tab in the trading terminal
 * @prop none - No external props, manages internal state and connector integration
 */

/**
 * @uiWorkflow
 * @name View Cryptocurrency Portfolio on Binance
 * @description How to monitor your crypto holdings and portfolio performance
 * @platform binance
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Holdings" tab to view your portfolio [component: Holdings] [action: click]
 * @step 3 Review your total portfolio value in USD [component: Holdings] [action: observe]
 * @step 4 Check your 24-hour profit/loss performance [component: Holdings] [action: observe] [notes: Green indicates profit, red indicates loss]
 * @step 5 Examine individual crypto holdings in the table [component: Holdings] [action: scroll]
 * @step 6 Compare available vs locked balances for each asset [component: Holdings] [action: observe]
 * @step 7 Monitor real-time price changes and 24h movements [component: Holdings] [action: observe]
 * @step 8 View portfolio allocation percentages for each asset [component: Holdings] [action: observe]
 */

/**
 * @uiWorkflow
 * @name Analyze Individual Crypto Asset Performance
 * @description How to evaluate specific cryptocurrency performance within your portfolio
 * @platform binance
 * @step 1 Open the Holdings tab in the trading terminal [component: Holdings] [action: click]
 * @step 2 Locate the specific cryptocurrency in your holdings table [component: Holdings] [action: scroll]
 * @step 3 Review the current price in USDT [component: Holdings] [action: observe]
 * @step 4 Check the 24-hour price change percentage [component: Holdings] [action: observe]
 * @step 5 Examine your 24-hour P&L for that specific asset [component: Holdings] [action: observe]
 * @step 6 View your allocation percentage within the total portfolio [component: Holdings] [action: observe]
 * @step 7 Note the available balance vs amount locked in orders [component: Holdings] [action: observe]
 * @step 8 Click on the asset row to access deposit/withdraw options [component: Holdings] [action: click]
 */

/**
 * @uiWorkflow
 * @name Access Deposit and Withdraw Functions for Crypto
 * @description How to manage deposits and withdrawals for specific cryptocurrencies
 * @platform binance
 * @step 1 Navigate to the Holdings tab [component: Holdings] [action: click]
 * @step 2 Find the cryptocurrency you want to deposit or withdraw [component: Holdings] [action: scroll]
 * @step 3 Click on the asset row to open the deposit/withdraw modal [component: Holdings] [action: click]
 * @step 4 The modal will display deposit and withdrawal options [component: DepositWithdrawModal] [action: observe]
 * @step 5 Choose between deposit or withdraw operations [component: DepositWithdrawModal] [action: select]
 * @step 6 Follow the prompts for wallet addresses or amounts [component: DepositWithdrawModal] [action: input]
 * @step 7 Complete the transaction as directed [component: DepositWithdrawModal] [action: submit]
 * @step 8 Close the modal and monitor balance updates [component: Holdings] [action: observe]
 */

interface AssetHolding {
  asset: string
  free: number
  locked: number
  total: number
  priceInUsdt: number
  usdValue: number
  // Enhanced with CoinGecko data
  logo?: string
  priceChange24h?: number
  pnl24h?: number
}

export default function Holdings() {
  const [selectedAsset, setSelectedAsset] = useState<string>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const pathname = usePathname()
  
  // Use React Query hooks instead of useEffect for data fetching
  const { data: rawAssets = [], isLoading, error } = useBinanceUserAssets()
  
  // Use the CoinGecko hook to get price data
  const { prices: coinGeckoData, loading: coinGeckoLoading } = useCoinGeckoPrices(1, 250)

  // Enhance assets with CoinGecko data using useMemo instead of useEffect
  const assets = useMemo((): AssetHolding[] => {
    if (!rawAssets.length || coinGeckoLoading) {
      return []
    }

    return rawAssets.map(asset => {
      // Find matching coin in CoinGecko data
      const coinData = coinGeckoData.find(
        coin => coin.symbol.toLowerCase() === asset.asset.toLowerCase()
      )

      // Calculate 24h P&L if we have price change data
      let pnl24h = 0
      if (coinData?.price_change_percentage_24h) {
        // Calculate what the price was 24h ago
        const priceChange = coinData.price_change_percentage_24h / 100
        const price24hAgo = asset.priceInUsdt / (1 + priceChange)

        // Calculate P&L
        pnl24h = asset.total * (asset.priceInUsdt - price24hAgo)
      }

      // Use public path for crypto icons
      const cryptoIconPath = `/img/crypto-icons/${asset.asset.toLowerCase()}.svg`

      return {
        ...asset,
        logo: coinData?.image || cryptoIconPath,
        priceChange24h: coinData?.price_change_percentage_24h || 0,
        pnl24h
      }
    })
  }, [rawAssets, coinGeckoData, coinGeckoLoading])

  // Calculate totals using useMemo for performance
  const { totalValue, total24hPnL } = useMemo(() => {
    const totalVal = assets.reduce((sum, asset) => sum + asset.usdValue, 0)
    const totalPnL = assets.reduce((sum, asset) => sum + (asset.pnl24h || 0), 0)
    
    return {
      totalValue: totalVal,
      total24hPnL: totalPnL
    }
  }, [assets])

  const binanceConnector = useMemo(() => {
    const { ConnectorManager } = require('../ConnectorManager')
    const { getSecureItem } = require('../utils/secureStorage')
    const connectorManager = ConnectorManager.getInstance()

    // Get the enabled connector (binance or ninx)
    // If on /x/ routes, default to ninx
    const isOnXRoute = pathname?.startsWith('/x/')
    const enabledConnector = isOnXRoute ? 'ninx' : (getSecureItem('broker_enabled') || 'ninx')
    const connector = enabledConnector === 'binance' ?
      connectorManager.getConnector('binance') :
      enabledConnector === 'ninx' ?
        connectorManager.getConnector('ninx') :
        connectorManager.getConnector('binance') // Fallback to binance
    
    return connector
  }, [pathname])

  if (isLoading || coinGeckoLoading) {
    return <div className={styles.loadingState}>Loading holdings...</div>
  }
  
  if (error) {
    return <div className={styles.errorState}>{error instanceof Error ? error.message : String(error)}</div>
  }
  
  if (assets.length === 0) {
    return <div className={styles.emptyState}>No holdings found</div>
  }

  return (
    <div className={styles.holdingsContainer}>
      {/* Deposit/Withdraw Modal */}
      {isModalOpen && (
        <DepositWithdrawModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          asset={selectedAsset}
          binanceConnector={binanceConnector}
        />
      )}
      <div className={styles.holdingsSummary}>
        <div className={styles.summaryCard}>
          <h3>Total Portfolio Value</h3>
          <p>
            $
            {totalValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
        </div>
        <div className={styles.summaryCard}>
          <h3>24h P&L</h3>
          <p className={total24hPnL >= 0 ? styles.profit : styles.loss}>
            $
            {total24hPnL.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            <span className={styles.changePercentage}>
              (
              {totalValue > 0
                ? ((total24hPnL / totalValue) * 100).toFixed(2)
                : '0.00'}
              %)
            </span>
          </p>
        </div>
      </div>
      <div className={styles.holdingsTable}>
        <table>
          <thead>
            <tr>
              <th>Asset</th>
              <th>Total</th>
              <th>Available</th>
              <th>In Order</th>
              <th>Price (USDT)</th>
              <th>24h Change</th>
              <th>Value (USDT)</th>
              <th>24h P&L</th>
              <th>Allocation</th>
            </tr>
          </thead>
          <tbody>
            {assets.map(asset => {
              const allocationPercentage = (asset.usdValue / totalValue) * 100

              return (
                <tr
                  key={asset.asset}
                  onClick={() => {
                    setSelectedAsset(asset.asset)
                    setIsModalOpen(true)
                  }}
                  className={styles.assetRow}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={styles.assetCell}>
                    {asset.logo && (
                      <Image
                        src={asset.logo}
                        alt={asset.asset}
                        width={24}
                        height={24}
                        className={styles.assetLogo}
                        onError={e => {
                          // If image fails to load, try fallback
                          const target = e.target as HTMLImageElement
                          target.onerror = null // Prevent infinite loop

                          // Try the PNG version if SVG fails
                          if (target.src.includes('.svg')) {
                            target.src = `/img/crypto-icons/${asset.asset.toLowerCase()}.png`
                          } else {
                            // If that also fails, use a generic fallback
                            target.src = `/img/crypto-icons/generic.svg`
                          }
                        }}
                        unoptimized={asset.logo.startsWith('http')} // Skip optimization for external URLs
                      />
                    )}
                    {asset.asset}
                  </td>
                  <td>{asset.total.toFixed(8)}</td>
                  <td>{asset.free.toFixed(8)}</td>
                  <td>{asset.locked.toFixed(8)}</td>
                  <td>
                    $
                    {asset.priceInUsdt.toFixed(
                      asset.priceInUsdt < 1 ? 8 : 2
                    )}
                  </td>
                  <td
                    className={
                      (asset.priceChange24h ?? 0) >= 0
                        ? styles.profit
                        : styles.loss
                    }
                  >
                    {asset.priceChange24h?.toFixed(2)}%
                  </td>
                  <td>
                    $
                    {asset.usdValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td
                    className={
                      (asset.pnl24h ?? 0) >= 0 ? styles.profit : styles.loss
                    }
                  >
                    $
                    {asset.pnl24h?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td>{allocationPercentage.toFixed(2)}%</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
